import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import AssignVariantDialog from "@dashboard/components/AssignVariantDialog";
import { DashboardCard } from "@dashboard/components/Card";
import { CardSpacer } from "@dashboard/components/CardSpacer";
import { ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton";
import FormSpacer from "@dashboard/components/FormSpacer";
import { DetailPageLayout } from "@dashboard/components/Layouts";
import { Pill } from "@dashboard/components/Pill";
import { Savebar } from "@dashboard/components/Savebar";
import { WsmErrorFragment, WsmKitDiscountKind, WsmKitRuleKind } from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import useProductSearch from "@dashboard/searches/useProductSearch";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import { getWsmKitsErrorMessage } from "@dashboard/wsmKits/errors";
import { sectionMessages, statusMessages } from "@dashboard/wsmKits/messages";
import { WsmKitFormData, WsmKitMemberDraft, WsmKitRuleDraft } from "@dashboard/wsmKits/types";
import { wsmKitListPath } from "@dashboard/wsmKits/urls";
import { getNextMemberSortOrder, makeDraftKey } from "@dashboard/wsmKits/utils";
import { Box, Checkbox, Input, Select, Text } from "@saleor/macaw-ui-next";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import WsmKitMembersCard from "../WsmKitMembersCard/WsmKitMembersCard";
import WsmKitRulesCard from "../WsmKitRulesCard/WsmKitRulesCard";
import { messages } from "./messages";

export interface WsmKitDetailsPageProps {
  title: string;
  collectionName?: string | null;
  currencyCode?: string | null;
  initialData: WsmKitFormData;
  errors: WsmErrorFragment[];
  disabled: boolean;
  saveButtonBarState: ConfirmButtonTransitionState;
  onSubmit: (data: WsmKitFormData) => void;
  onDelete?: () => void;
  header?: React.ReactNode;
  /** Create only: the collection picker. A saved kit is keyed by its collection. */
  collectionField?: React.ReactNode;
}

const getFieldError = (errors: WsmErrorFragment[], field: string) =>
  errors.find(error => error.field === field);

export const WsmKitDetailsPage = ({
  title,
  collectionName,
  currencyCode,
  initialData,
  errors,
  disabled,
  saveButtonBarState,
  onSubmit,
  onDelete,
  header,
  collectionField,
}: WsmKitDetailsPageProps) => {
  const intl = useIntl();
  const navigate = useNavigator();
  const [data, setData] = useState<WsmKitFormData>(initialData);
  const [pickerOpen, setPickerOpen] = useState(false);
  const { search: searchProducts, result: searchProductsOpts } = useProductSearch({
    variables: { after: null, first: 20, query: "" },
  });
  const change = (patch: Partial<WsmKitFormData>) => setData(current => ({ ...current, ...patch }));
  const assignedVariantIds = data.members.map(member => member.variantId);
  const products = (mapEdgesToItems(searchProductsOpts.data?.search) ?? []).map(product => ({
    ...product,
    variants: (product.variants ?? []).filter(variant => !assignedVariantIds.includes(variant.id)),
  }));
  const handleMembersAssign = (containers: Array<Record<string, any>>) => {
    const nextSortOrder = getNextMemberSortOrder(data.members);
    const added: WsmKitMemberDraft[] = containers
      .filter(container => !assignedVariantIds.includes(container.id))
      .map((container, index) => ({
        key: makeDraftKey("member"),
        id: null,
        variantId: container.id,
        variantName: container.name ?? "",
        sku: container.sku ?? "",
        productName: container.product?.name ?? container.name ?? "",
        quantity: 1,
        sortOrder: nextSortOrder + index,
        label: "",
        labelTouched: false,
      }));

    change({ members: [...data.members, ...added] });
    setPickerOpen(false);
  };
  const handleMemberRemove = (key: string) =>
    change({
      members: data.members.filter(member => member.key !== key),
      // A rule left pointing at a removed member is exactly the state
      // RULE_TARGET_NOT_IN_KIT refuses, so it is cleaned up here.
      rules: data.rules
        .filter(rule => rule.subjectKey !== key)
        .map(rule => ({
          ...rule,
          targetKeys: rule.targetKeys.filter(targetKey => targetKey !== key),
        })),
    });
  const handleRuleAdd = () => {
    const subject = data.members[0];
    const target = data.members[1];

    if (!subject || !target) {
      return;
    }

    const rule: WsmKitRuleDraft = {
      key: makeDraftKey("rule"),
      id: null,
      subjectKey: subject.key,
      kind: WsmKitRuleKind.REQUIRES_ONE_OF,
      targetKeys: [],
      message: "",
    };

    change({ rules: [...data.rules, rule] });
  };
  const discountAmountError = getFieldError(errors, "discountAmount");
  const freightClassError = getFieldError(errors, "freightClass");
  const isPercent = data.discountKind === WsmKitDiscountKind.PERCENT;

  return (
    <DetailPageLayout>
      <TopNav href={wsmKitListPath} title={title}>
        {header}
      </TopNav>
      <DetailPageLayout.Content>
        <DashboardCard data-test-id="wsm-kit-general">
          <DashboardCard.Header>
            <DashboardCard.Title>{intl.formatMessage(messages.generalTitle)}</DashboardCard.Title>
          </DashboardCard.Header>
          <DashboardCard.Content>
            {collectionField}
            <Box display="grid" __gridTemplateColumns="1fr 1fr" gap={4}>
              <Select
                label={intl.formatMessage(messages.discountKind)}
                data-test-id="wsm-kit-discount-kind"
                disabled={disabled}
                options={[
                  {
                    value: WsmKitDiscountKind.FIXED,
                    label: intl.formatMessage(sectionMessages.discountFixed),
                  },
                  {
                    value: WsmKitDiscountKind.PERCENT,
                    label: intl.formatMessage(sectionMessages.discountPercent),
                  },
                ]}
                value={data.discountKind}
                onChange={value => change({ discountKind: value as WsmKitDiscountKind })}
              />
              <Input
                label={intl.formatMessage(messages.discountAmount)}
                data-test-id="wsm-kit-discount-amount"
                type="number"
                disabled={disabled}
                value={data.discountAmount}
                onChange={event => change({ discountAmount: event.target.value })}
                error={!!discountAmountError}
                // The unit is an adornment on the field, never part of its label.
                endAdornment={
                  <Text marginRight={2} color="default2">
                    {isPercent ? "%" : (currencyCode ?? "")}
                  </Text>
                }
                helperText={
                  getWsmKitsErrorMessage(discountAmountError, intl) ??
                  intl.formatMessage(messages.discountAmountHelp)
                }
              />
            </Box>
            <FormSpacer />
            <Input
              label={intl.formatMessage(messages.freightClass)}
              data-test-id="wsm-kit-freight-class"
              disabled={disabled}
              value={data.freightClass}
              onChange={event => change({ freightClass: event.target.value })}
              error={!!freightClassError}
              helperText={
                getWsmKitsErrorMessage(freightClassError, intl) ??
                intl.formatMessage(messages.freightClassHelp)
              }
            />
          </DashboardCard.Content>
        </DashboardCard>
        <CardSpacer />
        <WsmKitMembersCard
          members={data.members}
          errors={errors}
          disabled={disabled}
          onAdd={() => setPickerOpen(true)}
          onRemove={handleMemberRemove}
          onQuantityChange={(key, quantity) =>
            change({
              members: data.members.map(member =>
                member.key === key ? { ...member, quantity } : member,
              ),
            })
          }
          onLabelChange={(key, label) =>
            change({
              members: data.members.map(member =>
                member.key === key ? { ...member, label, labelTouched: true } : member,
              ),
            })
          }
        />
        <CardSpacer />
        <WsmKitRulesCard
          members={data.members}
          rules={data.rules}
          errors={errors}
          disabled={disabled}
          onAdd={handleRuleAdd}
          onRemove={key => change({ rules: data.rules.filter(rule => rule.key !== key) })}
          onChange={(key, patch) =>
            change({
              rules: data.rules.map(rule => (rule.key === key ? { ...rule, ...patch } : rule)),
            })
          }
        />
      </DetailPageLayout.Content>

      <DetailPageLayout.RightSidebar>
        <DashboardCard data-test-id="wsm-kit-availability">
          <DashboardCard.Header>
            <DashboardCard.Title>
              {intl.formatMessage(messages.availabilityTitle)}
            </DashboardCard.Title>
          </DashboardCard.Header>
          <DashboardCard.Content>
            <Pill
              data-test-id="wsm-kit-status"
              color={data.active ? "success" : "error"}
              label={intl.formatMessage(data.active ? statusMessages.live : statusMessages.draft)}
            />
            <FormSpacer />
            <Checkbox
              data-test-id="wsm-kit-active"
              disabled={disabled}
              name="active"
              checked={data.active}
              onCheckedChange={checked => change({ active: !!checked })}
            >
              <Text size={3}>{intl.formatMessage(messages.active)}</Text>
            </Checkbox>
          </DashboardCard.Content>
        </DashboardCard>
        <CardSpacer />
        <DashboardCard data-test-id="wsm-kit-summary">
          <DashboardCard.Header>
            <DashboardCard.Title>{intl.formatMessage(messages.summaryTitle)}</DashboardCard.Title>
          </DashboardCard.Header>
          <DashboardCard.Content>
            {collectionName && (
              <>
                <Text size={2} color="default2" display="block">
                  {intl.formatMessage(messages.collection)}
                </Text>
                <Text size={3} display="block" data-test-id="wsm-kit-collection">
                  {collectionName}
                </Text>
                <FormSpacer />
              </>
            )}
            <Text size={3} color="default2" display="block" data-test-id="wsm-kit-member-count">
              {intl.formatMessage(messages.memberCount, { count: data.members.length })}
            </Text>
          </DashboardCard.Content>
        </DashboardCard>
      </DetailPageLayout.RightSidebar>

      <AssignVariantDialog
        confirmButtonState="default"
        hasMore={searchProductsOpts.data?.search?.pageInfo?.hasNextPage ?? false}
        open={pickerOpen}
        onFetch={searchProducts}
        onFetchMore={() => undefined}
        loading={searchProductsOpts.loading}
        onClose={() => setPickerOpen(false)}
        onSubmit={handleMembersAssign}
        products={products as never}
      />

      <Savebar>
        {onDelete && <Savebar.DeleteButton onClick={onDelete} />}
        <Savebar.Spacer />
        <Savebar.CancelButton onClick={() => navigate(wsmKitListPath)} />
        <Savebar.ConfirmButton
          transitionState={saveButtonBarState}
          onClick={() => onSubmit(data)}
          disabled={disabled}
          data-test-id="wsm-kit-save"
        />
      </Savebar>
    </DetailPageLayout>
  );
};

export default WsmKitDetailsPage;
